import type {
  ClaudeUsage,
  CursorUsage,
  GrokUsage,
  OpenAIUsage,
  OpenCodeUsage,
  UsageResponse,
} from './protocol.ts';

/** Last home-screen usage snapshot. Shown immediately while `/api/usage` refreshes. */
export const USAGE_KEY = 'home-usage';

export function readStoredUsage(): UsageResponse | null {
  try {
    const raw = localStorage.getItem(USAGE_KEY);
    if (!raw) return null;
    return parseStoredUsage(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function storeUsage(usage: UsageResponse): void {
  try {
    localStorage.setItem(USAGE_KEY, JSON.stringify(usage));
  } catch {
    /* quota / private mode */
  }
}

export function parseStoredUsage(raw: unknown): UsageResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const cursor = parseCursor(o['cursor']);
  const claude = parseClaude(o['claude']);
  const grok = parseGrok(o['grok']);
  const openai = parseOpenAI(o['openai']);
  const opencode = parseOpenCode(o['opencode']);
  if (!cursor.ok && !claude.ok && !grok.ok && !openai.ok && !opencode.ok) return null;
  return { cursor, claude, grok, openai, opencode };
}

/**
 * A failed Claude (or Cursor, or Grok) fetch should not wipe a previously
 * shown meter. Home still paints last-known-ok until the next successful snapshot.
 */
export function mergeUsage(prev: UsageResponse | null, next: UsageResponse): UsageResponse {
  return {
    cursor: keepOk(prev?.cursor, next.cursor),
    claude: keepOk(prev?.claude, next.claude),
    grok: keepOk(prev?.grok, next.grok ?? { ok: false }),
    openai: keepOk(prev?.openai, next.openai ?? { ok: false }),
    opencode: keepOk(prev?.opencode, next.opencode ?? { ok: false }),
  };
}

function keepOk<T extends { ok: boolean }>(prev: T | undefined, next: T): T {
  return next.ok || !prev?.ok ? next : prev;
}

function parseCursor(raw: unknown): CursorUsage {
  if (!isRecord(raw)) return { ok: false };
  if (raw['ok'] !== true) return { ok: false };
  const startedAt = numOrNull(raw['startedAt']);
  const resetsAt = numOrNull(raw['resetsAt']);
  const autoPercent = numOrNull(raw['autoPercent']);
  const apiPercent = numOrNull(raw['apiPercent']);
  if (
    startedAt === undefined ||
    resetsAt === undefined ||
    autoPercent === undefined ||
    apiPercent === undefined
  ) {
    return { ok: false };
  }
  return { ok: true, startedAt, resetsAt, autoPercent, apiPercent };
}

function parseClaude(raw: unknown): ClaudeUsage {
  if (!isRecord(raw)) return { ok: false };
  if (raw['ok'] !== true) return { ok: false };
  const sessionPercent = numOrNull(raw['sessionPercent']);
  const weekPercent = numOrNull(raw['weekPercent']);
  const sessionResetsAt = numOrNull(raw['sessionResetsAt']);
  const weekResetsAt = numOrNull(raw['weekResetsAt']);
  if (
    sessionPercent === undefined ||
    weekPercent === undefined ||
    sessionResetsAt === undefined ||
    weekResetsAt === undefined
  ) {
    return { ok: false };
  }
  return { ok: true, sessionPercent, weekPercent, sessionResetsAt, weekResetsAt };
}

function parseGrok(raw: unknown): GrokUsage {
  if (!isRecord(raw) || raw['ok'] !== true) return { ok: false };
  const percent = numOrNull(raw['percent']);
  const startedAt = numOrNull(raw['startedAt']);
  const resetsAt = numOrNull(raw['resetsAt']);
  if (percent === undefined || startedAt === undefined || resetsAt === undefined) {
    return { ok: false };
  }
  return { ok: true, percent, startedAt, resetsAt };
}

function parseOpenAI(raw: unknown): OpenAIUsage {
  if (!isRecord(raw) || raw['ok'] !== true) return { ok: false };
  const primaryPercent = numOrNull(raw['primaryPercent']);
  const primaryResetsAt = numOrNull(raw['primaryResetsAt']);
  const primaryWindowSeconds = numOrNull(raw['primaryWindowSeconds']);
  const secondaryPercent = numOrNull(raw['secondaryPercent']);
  const secondaryResetsAt = numOrNull(raw['secondaryResetsAt']);
  const secondaryWindowSeconds = numOrNull(raw['secondaryWindowSeconds']);
  const usd = numOrNull(raw['usd']);
  const monthStart = numOrNull(raw['monthStart']);
  if (
    primaryPercent === undefined || primaryResetsAt === undefined || primaryWindowSeconds === undefined ||
    secondaryPercent === undefined || secondaryResetsAt === undefined || secondaryWindowSeconds === undefined ||
    usd === undefined || monthStart === undefined
  ) return { ok: false };
  return { ok: true, primaryPercent, primaryResetsAt, primaryWindowSeconds, secondaryPercent, secondaryResetsAt, secondaryWindowSeconds, usd, monthStart };
}

function parseOpenCode(raw: unknown): OpenCodeUsage {
  if (!isRecord(raw) || raw['ok'] !== true) return { ok: false };
  const rollingPercent = numOrNull(raw['rollingPercent']);
  const rollingResetsAt = numOrNull(raw['rollingResetsAt']);
  const weekPercent = numOrNull(raw['weekPercent']);
  const weekResetsAt = numOrNull(raw['weekResetsAt']);
  const monthPercent = numOrNull(raw['monthPercent']);
  const monthResetsAt = numOrNull(raw['monthResetsAt']);
  if (
    rollingPercent === undefined ||
    rollingResetsAt === undefined ||
    weekPercent === undefined ||
    weekResetsAt === undefined ||
    monthPercent === undefined ||
    monthResetsAt === undefined
  ) {
    return { ok: false };
  }
  return {
    ok: true,
    rollingPercent,
    rollingResetsAt,
    weekPercent,
    weekResetsAt,
    monthPercent,
    monthResetsAt,
  };
}

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return raw !== null && typeof raw === 'object';
}

function finiteNum(raw: unknown): number | undefined {
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
}

function numOrNull(raw: unknown): number | null | undefined {
  if (raw === null) return null;
  return finiteNum(raw);
}

export const FIVE_HOUR_MS = 5 * 60 * 60 * 1000;
export const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** How far `now` is through `[startedAt, resetsAt]`, 0–100. Null if the span is unknown. */
export function cycleElapsedPercent(
  startedAt: number | null,
  resetsAt: number | null,
  now = Date.now(),
): number | null {
  if (startedAt === null || resetsAt === null) return null;
  const span = resetsAt - startedAt;
  if (span <= 0) return null;
  return Math.round(Math.min(100, Math.max(0, ((now - startedAt) / span) * 100)));
}

/**
 * Elapsed share of a fixed-length window that ends at `resetsAt`.
 * A 24h window at hour 12 is 50.
 */
export function windowElapsedPercent(
  resetsAt: number | null,
  windowMs: number,
  now = Date.now(),
): number | null {
  if (resetsAt === null || windowMs <= 0) return null;
  return cycleElapsedPercent(resetsAt - windowMs, resetsAt, now);
}

/** Elapsed share of the calendar month ending at `resetsAt`. */
export function monthElapsedPercent(resetsAt: number | null, now = Date.now()): number | null {
  if (resetsAt === null) return null;
  const start = new Date(resetsAt);
  start.setMonth(start.getMonth() - 1);
  return cycleElapsedPercent(start.getTime(), resetsAt, now);
}

/** Remaining time until `resetsAt` as `H:MM`. Null if missing or already past. */
export function formatRemainingHms(resetsAt: number | null, now = Date.now()): string | null {
  if (resetsAt === null) return null;
  const ms = resetsAt - now;
  if (ms <= 0) return null;
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}`;
}

/** Meter label for the current Grok window: week, month, or generic credits. */
export function grokWindowLabel(usage: Extract<GrokUsage, { ok: true }>): string {
  if (usage.startedAt !== null && usage.resetsAt !== null) {
    const days = (usage.resetsAt - usage.startedAt) / 86_400_000;
    if (days > 0 && days <= 8) return 'Week';
    if (days > 8) return 'Month';
  }
  return 'Credits';
}
